import { useEffect, useState } from 'react'
import { Modal } from '@/shared/components/ui'
import { ContextGroupSelector } from './ContextGroupSelector'
import { getEnabledGroups } from './contextsService'
import { useTranslation, useUnitTranslation } from '@/shared/contexts/TranslationContext'

interface GroupUpdateModalProps {
  unitId: string
  currentContextKey?: string | null
  isOpen: boolean
  onClose: () => void
  onUpdateGroups: (unitId: string, groupIds: string[]) => Promise<void>
}

export function GroupUpdateModal({ unitId, currentContextKey, isOpen, onClose, onUpdateGroups }: GroupUpdateModalProps) {
  const { t } = useTranslation()
  const { tu } = useUnitTranslation()
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => {
    if (isOpen) {
      loadEnabledGroups()
    }
  }, [isOpen])

  useEffect(() => {
    // reset when context changes while modal is open
    setSelectedGroupIds([])
  }, [currentContextKey])

  const loadEnabledGroups = async () => {
    if (!unitId) return
    setIsLoading(true)
    try {
      const data = await getEnabledGroups(unitId)
      const ids = (data || []).map((g: any) => g.group_id)
      setSelectedGroupIds(ids)
    } catch (error) {
      console.error('Error loading enabled groups:', error)
      setSelectedGroupIds([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await onUpdateGroups(unitId, selectedGroupIds)
      onClose()
    } catch (error) {
      console.error('Error saving enabled groups:', error)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={tu('list.updateGroups')}
      fullScreen={isMobile}
      mobileTopAligned={isMobile}
      headerLeft={
        isMobile ? (
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100 transition-colors"
            aria-label={t('actions.cancel')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        ) : undefined
      }
      headerRight={
        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={isSaving || isLoading}
            className="flex items-center gap-2 text-emerald-600 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-100 disabled:opacity-40 transition-colors"
            aria-label={t('actions.save')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </button>
          {!isMobile && (
            <button
              onClick={onClose}
              className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
              aria-label={t('actions.close')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {tu('contexts.selectGroupsDescription')}
        </p>

        <ContextGroupSelector
          hideContextSelector={true}
          selectedContextKey={currentContextKey || undefined}
          selectedGroupIds={selectedGroupIds}
          onGroupsChange={(ids) => setSelectedGroupIds(ids)}
          className="pt-2"
        />
      </div>
    </Modal>
  )
}
